/**
 * BUILD-039 — an HQ permission change whose result is not (yet) known stays UNRESOLVED until a validated answer for the SAME request id settles it.
 *
 * WHAT SETTLES AN ATTEMPT (and only this):
 *   applied   a 2xx with a recorded outcome (`applied` / `saved` / `retired`, `replayed` when it was already recorded), or the check endpoint reporting the request
 *             `recorded`.
 *   refused   (a) a FIRST send's own definite 4xx - no earlier copy exists, so nothing can still commit; or
 *             (b) a refusal the platform RECORDED under the request id (`fenced:true`, or the check endpoint's `refused-fenced`): a durable terminal fence, so a delayed
 *                 earlier copy of the same request that arrives later replays that refusal and can never commit.
 *   everything else stays UNRESOLVED: transport failure, timeout, 5xx / 429 / 408, a malformed body, `OUTCOME_UNKNOWN`, and ANY OTHER refusal of a RETRY. An ordinary
 *   refusal of a retry proves nothing about an earlier copy that was delayed before it took the platform's lock (the refusal is not recorded, the state can change, and the
 *   earlier copy can still commit later). It is never reported as "not saved".
 *
 * THE RECORD. The frozen request id and payload are written to the browser's own durable storage BEFORE the request is sent, so a refresh, a remount, a tab switch or a
 * crash while it is in flight keeps the editor locked and re-sends exactly the same request. The record is scoped by environment (API origin) + the signed-in actor
 * (issuer + sub) + the target, so it is never replayed under another actor, environment or target. It holds NO credential: no token, no cookie, no password; only the
 * ids and choices that were in the request. It is a convenience lock for THIS browser, not a global security control: the platform's own request replay / fence is
 * the control, and another browser or device does not see this record.
 *
 * Pure data and storage, no framework imports, so it is tested directly.
 */
export interface UnresolvedChange<P = unknown> {
  requestId: string;
  /** the exact request body that was (or is about to be) sent: frozen, a retry sends THIS and never the current draft */
  payload: P;
  /** what was being changed, in words, for the message */
  label: string;
  /** `sending` = recorded and sent but no answer was processed (also what a page that was refreshed mid-flight finds); `unknown` = an answer was lost or did not settle it */
  state: 'sending' | 'unknown';
  recordedAt: number;
}
export interface AttemptStorage { getItem(key: string): string | null; setItem(key: string, value: string): void; removeItem(key: string): void }

const PREFIX = 'hq-attempt:v1|';
/** a key that names a credential, whatever its spelling (access_token, Refresh-Token, idToken, api_key, Authorization, ...). `previewToken` is a state-binding value, not a credential. */
const FORBIDDEN_KEY = { test: (k: string) => { const n = k.toLowerCase().replace(/[^a-z0-9]/g, ''); return n !== 'previewtoken' && /(password|passwd|secret|credential|cookie|authorization|apikey|token|bearer|session)/.test(n); } };
let storage: AttemptStorage | null = null;
let scope: string | null = null;
const cache = new Map<string, UnresolvedChange>();          // key (incl. scope) -> record; mirrors storage and is the fallback when storage is unavailable
const listeners = new Set<() => void>();
let storageProbed = false;
let version = 0;
const emit = () => { version++; listeners.forEach((l) => { try { l(); } catch { /* a listener must never break the store */ } }); };

function defaultStorage(): AttemptStorage | null {
  // Only READABILITY is probed here: storage that is full or write-blocked must still show the unresolved attempts already in it. Writability is verified by every new record (recordAttempt).
  try { const s = (globalThis as { localStorage?: AttemptStorage }).localStorage; if (!s) return null; s.getItem(`${PREFIX}probe`); return s; } catch { return null; }
}
function store(): AttemptStorage | null { if (!storageProbed) { storageProbed = true; if (storage === null) storage = defaultStorage(); } return storage; }
const fullKey = (key: string) => `${PREFIX}${scope}|${key}`;

/** Set the storage and/or the scope. Tests pass a fake storage; the app sets the scope once the signed-in actor is known. */
export function configureAttemptStore(opts: { storage?: AttemptStorage | null; scope?: string | null; reset?: boolean }): void {
  if (opts.reset) { cache.clear(); storageProbed = false; storage = null; scope = null; }
  if ('storage' in opts) { storage = opts.storage ?? null; storageProbed = true; }
  if ('scope' in opts) scope = opts.scope ?? null;
  emit();
}
/** The scope string for an actor in an environment. Never contains a token. */
export function attemptScopeOf(apiOrigin: string, issuer: unknown, subject: unknown): string | null {
  if (typeof issuer !== 'string' || typeof subject !== 'string' || !issuer || !subject || !apiOrigin) return null;
  return `${apiOrigin}|${issuer}|${subject}`;
}
export function setAttemptScope(next: string | null): void { if (scope !== next) { scope = next; emit(); } }
export function subscribeAttempts(fn: () => void): () => void { listeners.add(fn); return () => { listeners.delete(fn); }; }
export function attemptStoreVersion(): number { return version; }

function valid(v: unknown): v is UnresolvedChange {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return typeof o.requestId === 'string' && /^[0-9a-f-]{8,64}$/i.test(o.requestId) && !!o.payload && typeof o.payload === 'object' && typeof o.label === 'string'
    && (o.state === 'sending' || o.state === 'unknown') && typeof o.recordedAt === 'number';
}
function read(key: string): UnresolvedChange | null {
  if (scope === null) return null;                       // actor unknown: nothing is shown and nothing is replayed
  const fk = fullKey(key);
  try { const raw = store()?.getItem(fk); if (raw) { const v = JSON.parse(raw); if (valid(v)) { cache.set(fk, v); return v; } } } catch { /* unreadable record: fall back to the cache */ }
  const c = cache.get(fk); return c && valid(c) ? c : null;
}
function write(key: string, v: UnresolvedChange): void {
  const fk = fullKey(key); cache.set(fk, v);
  try { store()?.setItem(fk, JSON.stringify(v)); } catch { /* storage full or blocked: the page-lifetime cache still holds it */ }
}
function containsCredentialKey(v: unknown, depth = 0): boolean {
  if (!v || typeof v !== 'object' || depth > 6) return false;
  return Object.entries(v as Record<string, unknown>).some(([k, x]) => FORBIDDEN_KEY.test(k) || containsCredentialKey(x, depth + 1));
}

export function getUnresolved<P = unknown>(key: string): UnresolvedChange<P> | null { return read(key) as UnresolvedChange<P> | null; }

/**
 * Record the attempt BEFORE it is sent. Returns the record, or null when it must NOT be sent: no known actor, an unresolved attempt already exists for this target under a
 * different request id (a new review or payload can never replace it), or the payload carries something that looks like a credential.
 */
export function recordAttempt<P extends object>(key: string, change: { requestId: string; payload: P; label: string }): UnresolvedChange<P> | null {
  lastFailure = null;
  if (scope === null) { lastFailure = 'no-actor'; return null; }
  const existing = read(key);
  if (existing) { if (existing.requestId === change.requestId) return existing as UnresolvedChange<P>; lastFailure = 'other-attempt'; return null; }
  if (containsCredentialKey(change.payload)) { lastFailure = 'credential'; return null; }
  const rec: UnresolvedChange<P> = { requestId: change.requestId, payload: JSON.parse(JSON.stringify(change.payload)), label: change.label, state: 'sending', recordedAt: Date.now() };
  // DURABLE or nothing. A new consequential change is only sent when the record is really in the browser's storage and reads back identically; otherwise it is NOT sent
  // (a page-lifetime memory copy would be lost by a reload, which is exactly when the record is needed). Existing records are never touched by a failed attempt.
  const s = store();
  if (!s) { lastFailure = 'storage-unavailable'; return null; }
  const fk = fullKey(key); const json = JSON.stringify(rec);
  try {
    s.setItem(fk, json);
    if (s.getItem(fk) !== json) throw new Error('read-back mismatch');
  } catch {
    try { s.removeItem(fk); } catch { /* nothing else to do */ }
    lastFailure = 'storage-failed'; return null;
  }
  cache.set(fk, rec); emit();
  return rec;
}
export type RecordFailure = 'no-actor' | 'other-attempt' | 'credential' | 'storage-unavailable' | 'storage-failed';
let lastFailure: RecordFailure | null = null;
/** why the last recordAttempt returned null (cleared by the next call) */
export function lastRecordFailure(): RecordFailure | null { return lastFailure; }
export const STORAGE_FAILURE_TEXT = 'This browser cannot keep a durable record of the change (its storage is unavailable, full or blocked), so NOTHING was sent. Any change that is already unresolved is kept. Free some browser storage or use a normal (not private) window, then try again.';
/** the plain sentence for a change that could not be started; `kind` is lastRecordFailure() */
export function notStartedText(kind: string | null | undefined): string {
  if (kind === 'storage-unavailable' || kind === 'storage-failed') return STORAGE_FAILURE_TEXT;
  if (kind === 'other-attempt') return 'Another change for this target has an unknown result and must be settled first, so nothing was sent.';
  if (kind === 'no-actor') return 'The signed-in person could not be confirmed, so nothing was sent. Reload the page and try again.';
  return 'The change could not be started safely, so nothing was sent. Reload the page and try again.';
}
/** The answer did not settle it: it stays recorded, now as unknown. */
export function markUnknown(key: string, requestId: string): void { const v = read(key); if (v && v.requestId === requestId && v.state !== 'unknown') { write(key, { ...v, state: 'unknown' }); } emit(); }
/** A validated answer settled it. Only the SAME request id can clear it. */
export function clearAttempt(key: string, requestId: string): void {
  const v = read(key); if (!v || v.requestId !== requestId) return;
  const fk = fullKey(key); cache.delete(fk); try { store()?.removeItem(fk); } catch { /* nothing else to do */ }
  emit();
}
/** keys (without the scope) of every unresolved attempt of the current actor that start with a prefix, e.g. `role:` */
export function unresolvedKeys(prefix: string): string[] {
  if (scope === null) return [];
  const head = `${PREFIX}${scope}|`; const out = new Set<string>();
  const add = (fk: string) => { if (fk.startsWith(head) && fk.slice(head.length).startsWith(prefix) && read(fk.slice(head.length))) out.add(fk.slice(head.length)); };
  try { const s = store() as (AttemptStorage & { length?: number; key?: (i: number) => string | null }) | null; if (s && typeof s.length === 'number' && s.key) for (let i = 0; i < s.length; i++) { const k = s.key(i); if (k) add(k); } } catch { /* use the cache */ }
  for (const fk of cache.keys()) add(fk);
  return [...out];
}

export type AttemptVerdict = 'applied' | 'refused' | 'unresolved';
const APPLIED_OUTCOMES = new Set(['applied', 'saved', 'retired']);
/** What a response to the change request proves. `earlierCopyMayExist` is true for every send except the very first one of a freshly recorded attempt. */
export function classifyHqResponse(r: { ok?: boolean; status?: number; body?: any } | null | undefined, earlierCopyMayExist: boolean): AttemptVerdict {
  if (!r || typeof r !== 'object') return 'unresolved';
  const body = r.body && typeof r.body === 'object' ? r.body : null;
  const status = typeof r.status === 'number' ? r.status : undefined;
  if (body && (body.error === 'OUTCOME_UNKNOWN' || body.outcome === 'unknown')) return 'unresolved';
  if (r.ok === true) return body && typeof body.outcome === 'string' && APPLIED_OUTCOMES.has(body.outcome) ? 'applied' : 'unresolved';
  if (status === undefined || status < 400 || status >= 500 || status === 408 || status === 429) return 'unresolved';
  // a refusal the platform RECORDED under this request id: a terminal fence, whoever asks and whenever
  if (body && body.outcome === 'refused' && body.fenced === true && typeof body.reason === 'string' && body.reason) return 'refused';
  return earlierCopyMayExist ? 'unresolved' : 'refused';
}
/** What the lost-answer check proves. */
export function classifyHqCheck(r: { ok?: boolean; body?: any } | null | undefined): AttemptVerdict {
  const b = r && r.ok === true && r.body && typeof r.body === 'object' ? r.body : null;
  if (!b || b.final !== true) return 'unresolved';
  if (b.state === 'recorded' && b.applied === true) return 'applied';
  if (b.state === 'refused-fenced') return 'refused';
  return 'unresolved';
}

export const NO_RECORD_YET_TEXT =
  'There is no record of this change yet. That is not proof either way: the first attempt may still arrive. Nothing else can be changed meanwhile. '
  + 'Use "Save again (same change)": it is applied once, or the recorded result is returned. This clears only when the exact result is known.';
